import { useState, useCallback } from "react";
import { useFaust } from "../../contexts/FaustContext";
import { useCompileOptions } from "../../hooks/useCompileOptions";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Compass } from "lucide-react";

export function PlotSettings() {
    const { analyserRef, editorRef, runDsp, dispatch } = useFaust();
    const { getOptions, setOption } = useCompileOptions();

    const opts = getOptions();
    const [plotMode, setPlotMode] = useState(opts.plotMode);
    const [plotSamps, setPlotSamps] = useState(String(opts.plot));
    const [plotSR, setPlotSR] = useState(String(opts.plotSR));
    const [fftSize, setFftSize] = useState(String(opts.plotFFT));
    const [fftOverlap, setFftOverlap] = useState(String(opts.plotFFTOverlap));
    const [drawSpectrogram, setDrawSpectrogram] = useState(opts.drawSpectrogram);

    const handlePlotModeChange = useCallback(
        (v: string) => {
            setPlotMode(v as any);
            setOption("plotMode", v as any);
            analyserRef.current.drawMode = v as any;
        },
        [setOption, analyserRef]
    );

    const handlePlot = useCallback(async () => {
        const editor = editorRef.current;
        if (!editor) return;

        if (plotMode === "offline") {
            // Offline plot - compile and render
            const code = editor.getValue();
            const opts = getOptions();
            const { FaustMonoDspGenerator, FaustCompiler } = await import("@grame/faustwasm");
            // This will use the compiler from context
            dispatch({ type: "SET_ACTIVE_BOTTOM_TAB", tab: "plot" });
        } else {
            dispatch({ type: "SET_ACTIVE_BOTTOM_TAB", tab: "plot" });
        }
    }, [plotMode, editorRef, getOptions, dispatch]);

    return (
        <div className="flex flex-col gap-2.5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                Plot
            </span>

            {/* Mode */}
            <div className="flex flex-col gap-1">
                <Label className="text-xs text-zinc-500">Mode</Label>
                <Select value={plotMode} onValueChange={handlePlotModeChange}>
                    <SelectTrigger className="h-7 text-xs bg-[#1f1f1f] border-white/[0.08]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="offline">Offline</SelectItem>
                        <SelectItem value="continuous">Continuous</SelectItem>
                        <SelectItem value="onevent">On Event</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Samples */}
            <div className="flex flex-col gap-1">
                <Label className="text-xs text-zinc-500">Samples</Label>
                <Input
                    type="number"
                    value={plotSamps}
                    onChange={(e) => {
                        setPlotSamps(e.target.value);
                        const v = Number(e.target.value);
                        if (v > 0) setOption("plot", v);
                    }}
                    min={0}
                    step={128}
                    className="h-7 text-xs bg-[#1f1f1f] border-white/[0.08]"
                />
            </div>

            {/* Sample Rate */}
            <div className="flex flex-col gap-1">
                <Label className="text-xs text-zinc-500">Sample Rate</Label>
                <Input
                    type="number"
                    value={plotSR}
                    onChange={(e) => {
                        setPlotSR(e.target.value);
                        const v = Number(e.target.value);
                        if (v > 0) setOption("plotSR", v);
                    }}
                    min={100}
                    step={100}
                    disabled={plotMode !== "offline"}
                    className="h-7 text-xs bg-[#1f1f1f] border-white/[0.08]"
                />
            </div>

            {/* FFT Size */}
            <div className="flex flex-col gap-1">
                <Label className="text-xs text-zinc-500">FFT Size</Label>
                <Select
                    value={fftSize}
                    onValueChange={(v) => {
                        setFftSize(v);
                        const n = Number(v);
                        setOption("plotFFT", n);
                        analyserRef.current.fftSize = n;
                    }}
                >
                    <SelectTrigger className="h-7 text-xs bg-[#1f1f1f] border-white/[0.08]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {[256, 512, 1024, 2048, 4096, 8192, 16384, 32768, 65536].map((v) => (
                            <SelectItem key={v} value={String(v)}>
                                {v}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* FFT Overlap */}
            <div className="flex flex-col gap-1">
                <Label className="text-xs text-zinc-500">FFT Overlap</Label>
                <Select
                    value={fftOverlap}
                    onValueChange={(v) => {
                        setFftOverlap(v);
                        const n = Number(v);
                        setOption("plotFFTOverlap", n);
                        analyserRef.current.fftOverlap = n;
                    }}
                >
                    <SelectTrigger className="h-7 text-xs bg-[#1f1f1f] border-white/[0.08]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="4">4</SelectItem>
                        <SelectItem value="8">8</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Draw Spectrogram */}
            <div className="flex items-center gap-2">
                <Checkbox
                    id="spectrogram"
                    checked={drawSpectrogram}
                    onCheckedChange={(v) => {
                        setDrawSpectrogram(!!v);
                        setOption("drawSpectrogram", !!v);
                    }}
                    className="h-3.5 w-3.5"
                />
                <Label htmlFor="spectrogram" className="text-xs text-zinc-400 cursor-pointer">
                    Draw Spectrogram
                </Label>
            </div>

            {/* Plot Button */}
            {(plotMode === "offline" || plotMode === "manual") && (
                <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-7 text-xs border-zinc-700 text-zinc-400 hover:text-zinc-100"
                    onClick={handlePlot}
                >
                    <Compass className="h-3 w-3 mr-1.5" />
                    {plotMode === "offline" ? "Plot First Samples" : "Plot (Snapshot)"}
                </Button>
            )}
        </div>
    );
}
